import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { API, useAuth, hasPermission } from "@/App";
import axios from "axios";
import {
  LayoutDashboard, ShoppingCart, Store, Package, Boxes, PackageX, Truck, Route,
  MapPin, Receipt, Users, PhoneCall, CalendarClock, FileText, AlertCircle, Eye,
  ListTodo, Share2, Megaphone, MessageSquare, Gift, Trash2, Car, PackagePlus,
  Settings, Smartphone, Sparkles, BarChart3, Search, Building2, ChevronRight, Star, ShieldCheck,
} from "lucide-react";

/*
 * Palette lavande dérivée du logo TopCocoon (#8B9FE8).
 * Chaque tuile a une teinte icône dans le spectre indigo/violet/ciel,
 * pour rester cohérente avec l'identité visuelle.
 */
const APPS = [
  { key: "dashboard-stats",  path: "/statistiques",  label: "Statistiques", icon: BarChart3,    tone: "from-indigo-100 to-indigo-50",   iconClr: "text-indigo-600",   permission: "dashboard" },
  { key: "ventes",           path: "/ventes",        label: "Ventes",       icon: ShoppingCart, tone: "from-violet-100 to-violet-50",   iconClr: "text-violet-600",   permission: "ventes" },
  { key: "vente-depot",      path: "/vente-depot",   label: "Vente sur place", icon: Store,     tone: "from-fuchsia-100 to-fuchsia-50", iconClr: "text-fuchsia-600",  permission: "ventes" },
  { key: "stock",            path: "/stock",         label: "Stock",        icon: Package,      tone: "from-blue-100 to-blue-50",       iconClr: "text-blue-600",     permission: "stock" },
  { key: "produits",         path: "/produits",      label: "Produits",     icon: Boxes,        tone: "from-sky-100 to-sky-50",         iconClr: "text-sky-600",      permission: "produits" },
  { key: "pertes",           path: "/pertes",        label: "Pertes",       icon: PackageX,     tone: "from-rose-100 to-rose-50",       iconClr: "text-rose-500",     permission: "pertes" },
  { key: "livraisons",       path: "/livraisons",    label: "Livraisons",   icon: Truck,        tone: "from-indigo-100 to-indigo-50",   iconClr: "text-indigo-600",   permission: "livraisons",   badge: "livraisons_en_attente" },
  { key: "tournees",         path: "/tournees",      label: "Tournées",     icon: Route,        tone: "from-violet-100 to-violet-50",   iconClr: "text-violet-600",   permission: "tournees",     badge: "tournees_today" },
  { key: "zones",            path: "/zones",         label: "Carte des ventes", icon: MapPin,   tone: "from-emerald-100 to-emerald-50", iconClr: "text-emerald-600",  permission: "zones" },
  { key: "charges",          path: "/charges",       label: "Charges",      icon: Receipt,      tone: "from-amber-100 to-amber-50",     iconClr: "text-amber-600",    permission: "charges" },
  { key: "clients",          path: "/clients",       label: "Clients",      icon: Users,        tone: "from-cyan-100 to-cyan-50",       iconClr: "text-cyan-600",     permission: "clients" },
  { key: "rappels",          path: "/rappels",       label: "À rappeler",   icon: PhoneCall,    tone: "from-orange-100 to-orange-50",   iconClr: "text-orange-500",   permission: "rappels",      badge: "rappels_today" },
  { key: "rdv",              path: "/rendez-vous",   label: "Rendez-vous",  icon: CalendarClock,tone: "from-blue-100 to-blue-50",       iconClr: "text-blue-600",     permission: "rdv" },
  { key: "devis",            path: "/devis",         label: "Devis & Factures", icon: FileText, tone: "from-slate-100 to-slate-50",     iconClr: "text-slate-700",    permission: "devis" },
  { key: "impayes",          path: "/impayes",       label: "Impayés",      icon: AlertCircle,  tone: "from-red-100 to-red-50",         iconClr: "text-red-600",      permission: "impayes" },
  { key: "veille",           path: "/veille",        label: "Veille",       icon: Eye,          tone: "from-teal-100 to-teal-50",       iconClr: "text-teal-600",     permission: "veille" },
  { key: "taches",           path: "/taches",        label: "Tâches",       icon: ListTodo,     tone: "from-indigo-100 to-indigo-50",   iconClr: "text-indigo-600",   permission: "taches" },
  { key: "social",           path: "/social",        label: "Réseaux Sociaux", icon: Share2,    tone: "from-pink-100 to-pink-50",       iconClr: "text-pink-600",     permission: "social" },
  { key: "marketing",        path: "/marketing",     label: "Marketing",    icon: Megaphone,    tone: "from-fuchsia-100 to-fuchsia-50", iconClr: "text-fuchsia-600",  permission: "marketing" },
  { key: "communications",   path: "/communications",label: "Communications", icon: MessageSquare, tone: "from-violet-100 to-violet-50", iconClr: "text-violet-600", permission: "communications", badge: "communications" },
  { key: "parrainages",      path: "/parrainages",   label: "Parrainages",  icon: Gift,         tone: "from-rose-100 to-rose-50",       iconClr: "text-rose-500",     permission: "parrainages" },
  { key: "vehicules",        path: "/vehicules",     label: "Véhicules",    icon: Car,          tone: "from-slate-100 to-slate-50",     iconClr: "text-slate-700",    permission: "vehicules" },
  { key: "arrivages",        path: "/arrivages",     label: "Arrivages",    icon: PackagePlus,  tone: "from-emerald-100 to-emerald-50", iconClr: "text-emerald-600",  permission: "arrivages" },
  { key: "livreur",          path: "/livreur",       label: "Mode Livreur", icon: Smartphone,   tone: "from-green-100 to-green-50",     iconClr: "text-green-600",    permission: "livreur",      accent: true },
  { key: "ai_chef",          path: "/assistant-ia",  label: "Assistant IA", icon: Sparkles,     tone: "from-purple-100 to-purple-50",   iconClr: "text-purple-600",   permission: "ai_chef",      accent: true },
  { key: "corbeille",        path: "/corbeille",     label: "Corbeille",    icon: Trash2,       tone: "from-slate-100 to-slate-50",     iconClr: "text-slate-500",    permission: "corbeille" },
  { key: "parametres",       path: "/parametres",    label: "Paramètres",   icon: Settings,     tone: "from-slate-100 to-slate-50",     iconClr: "text-slate-600",    permission: "parametres" },
  { key: "audit",            path: "/audit",         label: "Journal d'audit", icon: ShieldCheck, tone: "from-emerald-100 to-emerald-50", iconClr: "text-emerald-600", permission: "parametres" },
  { key: "b2b_admin",        path: "/b2b-admin",     label: "Portail B2B",  icon: Building2,    tone: "from-indigo-100 to-indigo-50",   iconClr: "text-indigo-600",   permission: "parametres" },
];

// Regroupement desktop — les mêmes clés que APPS, permissions filtrées à l'affichage
const CATEGORIES = [
  { title: "Ventes & Clients", keys: ["ventes", "vente-depot", "clients", "rappels", "rdv", "devis", "impayes"] },
  { title: "Logistique",        keys: ["livraisons", "tournees", "zones", "arrivages", "vehicules"] },
  { title: "Gestion",           keys: ["produits", "stock", "charges", "pertes", "dashboard-stats", "veille"] },
  { title: "Marketing",         keys: ["marketing", "social", "communications", "parrainages"] },
  { title: "Outils",            keys: ["ai_chef", "b2b_admin", "parametres", "audit", "corbeille"] },
];

// Favoris par défaut (8 max) — modifiables ensuite via drag & drop dans "Personnaliser"
const DEFAULT_FAVORITES = ["ventes", "clients", "livraisons", "stock", "dashboard-stats", "b2b_admin", "livreur", "produits"];

// Actions "À traiter" — utilise les compteurs badges déjà chargés
const ACTIONABLES = [
  { key: "rappels",        path: "/rappels",        label: "À rappeler",              subtitle: "Aujourd'hui",        icon: PhoneCall,     badge: "rappels_today",         tone: "bg-orange-50 border-orange-200",  iconClr: "text-orange-600", numClr: "text-orange-700" },
  { key: "liv_today",      path: "/livraisons",     label: "Livraisons aujourd'hui",  subtitle: "À préparer",         icon: Truck,         badge: "livraisons_en_attente", tone: "bg-indigo-50 border-indigo-200",  iconClr: "text-indigo-600", numClr: "text-indigo-700" },
  { key: "communications", path: "/communications", label: "Communications",          subtitle: "En attente",         icon: MessageSquare, badge: "communications",        tone: "bg-violet-50 border-violet-200",  iconClr: "text-violet-600", numClr: "text-violet-700" },
  { key: "impayes",        path: "/impayes",        label: "Impayés",                 subtitle: "À encaisser",        icon: AlertCircle,   badge: "impayes",               tone: "bg-red-50 border-red-200",        iconClr: "text-red-600",    numClr: "text-red-700" },
  { key: "rdv",            path: "/rendez-vous",    label: "Rendez-vous",             subtitle: "Cette semaine",      icon: CalendarClock, badge: "rdv_today",             tone: "bg-blue-50 border-blue-200",      iconClr: "text-blue-600",   numClr: "text-blue-700" },
  { key: "stock_alertes",  path: "/stock",          label: "Stock critique",          subtitle: "Produits",           icon: Package,       badge: "stock_alertes",         tone: "bg-amber-50 border-amber-200",    iconClr: "text-amber-600",  numClr: "text-amber-700" },
];

const AppsHome = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [badges, setBadges] = useState({});
  const [editMode, setEditMode] = useState(false);
  const [customOrder, setCustomOrder] = useState(() => {
    try {
      const raw = localStorage.getItem("apps_home_order");
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });
  const [dragKey, setDragKey] = useState(null);
  const [dropOverKey, setDropOverKey] = useState(null);

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    const fetchBadges = async () => {
      try {
        const todayISO = new Date().toISOString().slice(0, 10);
        const [rapport, comSms, comEmail, impayes, rdv] = await Promise.all([
          axios.get(`${API}/rapport-journalier`, { headers }).catch(() => ({ data: {} })),
          axios.get(`${API}/communications/sms-pending`, { headers }).catch(() => ({ data: [] })),
          axios.get(`${API}/communications/emails-pending`, { headers }).catch(() => ({ data: [] })),
          axios.get(`${API}/impayes`, { headers }).catch(() => ({ data: [] })),
          axios.get(`${API}/rdv`, { headers }).catch(() => ({ data: [] })),
        ]);
        if (cancelled) return;
        const rdvToday = (rdv.data || []).filter((r) => r.date === todayISO && r.statut !== "annule").length;
        setBadges({
          livraisons_en_attente: rapport.data?.en_attente || 0,
          tournees_today: rapport.data?.tournees_today || 0,
          rappels_today: rapport.data?.stock_alertes || 0,
          communications: (comSms.data?.length || 0) + (comEmail.data?.length || 0),
          impayes: (impayes.data || []).length,
          rdv_today: rdvToday,
          stock_alertes: rapport.data?.stock_alertes || 0,
        });
      } catch { /* silent */ }
    };
    fetchBadges();
    return () => { cancelled = true; };
  }, [token, headers]);

  const persistOrder = (order) => {
    setCustomOrder(order);
    try { localStorage.setItem("apps_home_order", JSON.stringify(order)); } catch { /* ignore */ }
  };

  const visibleApps = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = APPS
      .filter(a => hasPermission(user, a.permission))
      .filter(a => !q || a.label.toLowerCase().includes(q));
    if (!customOrder.length) return filtered;
    const map = Object.fromEntries(filtered.map(a => [a.key, a]));
    const ordered = customOrder.map(k => map[k]).filter(Boolean);
    const remaining = filtered.filter(a => !customOrder.includes(a.key));
    return [...ordered, ...remaining];
  }, [user, query, customOrder]);

  // Favoris : 8 premiers modules dans l'ordre custom (ou DEFAULT_FAVORITES)
  const favApps = useMemo(() => {
    const apps = APPS.filter(a => hasPermission(user, a.permission));
    if (customOrder.length) {
      const map = Object.fromEntries(apps.map(a => [a.key, a]));
      const ordered = customOrder.map(k => map[k]).filter(Boolean);
      return ordered.slice(0, 8);
    }
    const map = Object.fromEntries(apps.map(a => [a.key, a]));
    return DEFAULT_FAVORITES.map(k => map[k]).filter(Boolean).slice(0, 8);
  }, [user, customOrder]);

  const catGroups = useMemo(() => {
    const q = query.trim().toLowerCase();
    return CATEGORIES.map(cat => {
      const apps = cat.keys
        .map(k => APPS.find(a => a.key === k))
        .filter(a => a && hasPermission(user, a.permission))
        .filter(a => !q || a.label.toLowerCase().includes(q));
      return { ...cat, apps };
    }).filter(g => g.apps.length > 0);
  }, [user, query]);

  const handleDragStart = (key) => setDragKey(key);
  const handleDragOver = (e) => e.preventDefault();
  const handleDragEnter = (key) => { if (dragKey && dragKey !== key) setDropOverKey(key); };
  const handleDragLeave = (key) => { if (dropOverKey === key) setDropOverKey(null); };
  const handleDragEnd = () => { setDragKey(null); setDropOverKey(null); };
  const handleDrop = (targetKey) => {
    if (!dragKey || dragKey === targetKey) { handleDragEnd(); return; }
    const currentKeys = visibleApps.map(a => a.key);
    const fromIdx = currentKeys.indexOf(dragKey);
    const toIdx = currentKeys.indexOf(targetKey);
    if (fromIdx < 0 || toIdx < 0) { handleDragEnd(); return; }
    const next = [...currentKeys];
    next.splice(fromIdx, 1);
    next.splice(toIdx, 0, dragKey);
    persistOrder(next);
    handleDragEnd();
  };

  const resetOrder = () => { persistOrder([]); };

  // Rendu compact d'une tuile (utilisé mobile + fallback edit-mode desktop)
  const renderTile = (app) => {
    const Icon = app.icon;
    const count = app.badge ? badges[app.badge] : 0;
    return (
      <button
        key={app.key}
        onClick={() => !editMode && navigate(app.path)}
        draggable={editMode}
        onDragStart={() => handleDragStart(app.key)}
        onDragOver={handleDragOver}
        onDragEnter={() => handleDragEnter(app.key)}
        onDragLeave={() => handleDragLeave(app.key)}
        onDragEnd={handleDragEnd}
        onDrop={() => handleDrop(app.key)}
        className={`group relative flex flex-col items-center justify-center gap-1.5 sm:gap-2 aspect-square rounded-xl sm:rounded-2xl bg-white border ${app.accent ? "border-indigo-200 ring-1 ring-indigo-100" : "border-slate-100"} shadow-sm ${editMode ? "cursor-grab active:cursor-grabbing ring-2 ring-indigo-200 ring-offset-1" : "hover:shadow-md hover:-translate-y-0.5 hover:border-indigo-200"} ${dragKey === app.key ? "opacity-40 scale-95" : ""} ${dropOverKey === app.key ? "ring-4 ring-indigo-500 ring-offset-2 scale-105 shadow-lg" : ""} transition-all duration-200 p-1.5 sm:p-2 text-center`}
        data-testid={`app-tile-${app.key}`}
      >
        {count > 0 && (
          <span className="absolute top-0.5 right-0.5 sm:top-1.5 sm:right-1.5 min-w-[16px] h-4 sm:min-w-[18px] sm:h-5 px-1 rounded-full bg-indigo-600 text-white text-[9px] sm:text-[10px] font-bold flex items-center justify-center shadow-sm" data-testid={`app-tile-badge-${app.key}`}>
            {count > 99 ? "99+" : count}
          </span>
        )}
        <div className={`w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 rounded-lg sm:rounded-xl bg-gradient-to-br ${app.tone} flex items-center justify-center group-hover:scale-110 transition-transform`}>
          <Icon className={`w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 ${app.iconClr}`} strokeWidth={2} />
        </div>
        <span className="text-[9px] sm:text-[10px] md:text-xs font-medium text-slate-700 leading-tight line-clamp-2 whitespace-normal">{app.label}</span>
      </button>
    );
  };

  // Tuile "premium" desktop — plus grande, avec label à côté de l'icône
  const renderDesktopTile = (app, size = "md") => {
    const Icon = app.icon;
    const count = app.badge ? badges[app.badge] : 0;
    const isLg = size === "lg";
    return (
      <button
        key={app.key}
        onClick={() => navigate(app.path)}
        className={`group relative flex items-center gap-3 ${isLg ? "p-4" : "p-3"} rounded-xl bg-white border ${app.accent ? "border-indigo-200 ring-1 ring-indigo-100" : "border-slate-100"} shadow-sm hover:shadow-md hover:border-indigo-200 hover:-translate-y-0.5 transition-all duration-200 text-left`}
        data-testid={`app-desktop-tile-${app.key}`}
      >
        <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${app.tone} flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform`}>
          <Icon className={`w-5 h-5 ${app.iconClr}`} strokeWidth={2.2} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm text-slate-800 leading-tight truncate">{app.label}</p>
        </div>
        {count > 0 && (
          <span className="min-w-[22px] h-5 px-1.5 rounded-full bg-indigo-600 text-white text-[10px] font-bold flex items-center justify-center shadow-sm">
            {count > 99 ? "99+" : count}
          </span>
        )}
        <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-indigo-500 group-hover:translate-x-0.5 transition" />
      </button>
    );
  };

  return (
    <div
      className="min-h-[calc(100vh-4rem)] -mx-6 -my-8 md:-mx-8 md:-my-8 px-4 sm:px-6 md:px-8 lg:px-10 py-6 sm:py-8"
      style={{ background: "linear-gradient(180deg, #F5F6FC 0%, #EEF0FA 100%)" }}
      data-testid="apps-home"
    >
      <div className="max-w-[1600px] mx-auto">
        {/* Header */}
        <div className="mb-6 lg:mb-8 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
          <div>
            <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-slate-800 tracking-tight">
              Bonjour{user?.name ? `, ${user.name.split(" ")[0]}` : ""} <span className="inline-block">👋</span>
            </h1>
            <p className="text-slate-500 mt-1 text-sm md:text-base hidden lg:block">Vos outils TopCocoon en un coup d&apos;œil.</p>
            <p className="text-slate-500 mt-0.5 text-xs sm:text-sm lg:hidden">Vos modules TopCocoon en un coup d&apos;œil.</p>
          </div>
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <div className="relative flex-1 sm:w-72">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Rechercher un module…"
                className="w-full pl-9 pr-3 py-2 sm:py-2.5 rounded-xl bg-white border border-slate-200 focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 outline-none text-sm shadow-sm"
                data-testid="apps-home-search"
              />
            </div>
            <button
              onClick={() => setEditMode(!editMode)}
              className={`px-3 py-2 sm:py-2.5 rounded-xl text-xs sm:text-sm font-medium shadow-sm transition ${editMode ? "bg-indigo-600 text-white" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"}`}
              data-testid="apps-home-edit-toggle"
              title="Réorganiser les modules"
            >
              {editMode ? "✓ OK" : "✎ Personnaliser"}
            </button>
            {editMode && customOrder.length > 0 && (
              <button
                onClick={resetOrder}
                className="px-3 py-2 sm:py-2.5 rounded-xl text-xs sm:text-sm bg-slate-100 hover:bg-slate-200 text-slate-600 shadow-sm"
                data-testid="apps-home-reset-order"
                title="Réinitialiser l'ordre par défaut"
              >
                ↺
              </button>
            )}
          </div>
        </div>

        {/* ========== DESKTOP LAYOUT ( lg+ , hors editMode ) ========== */}
        {!editMode && (
          <div className="hidden lg:block space-y-6" data-testid="apps-home-desktop">
            {/* À traiter — actions importantes */}
            <section className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm" data-testid="section-a-traiter">
              <h2 className="text-sm font-bold text-slate-700 flex items-center gap-2 mb-4">
                <AlertCircle className="w-4 h-4 text-indigo-600" /> À traiter
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
                {ACTIONABLES.filter((a) => hasPermission(user, a.key === "liv_today" ? "livraisons" : a.key === "stock_alertes" ? "stock" : a.key)).map((a) => {
                  const Icon = a.icon;
                  const count = badges[a.badge] || 0;
                  return (
                    <button
                      key={a.key}
                      onClick={() => navigate(a.path)}
                      className={`relative flex items-center gap-3 p-3.5 rounded-xl border ${a.tone} hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 text-left`}
                      data-testid={`actionable-${a.key}`}
                    >
                      <div className={`w-10 h-10 rounded-lg bg-white flex items-center justify-center flex-shrink-0 shadow-sm`}>
                        <Icon className={`w-5 h-5 ${a.iconClr}`} strokeWidth={2.2} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-slate-600 font-medium truncate">{a.label}</p>
                        <p className={`text-2xl font-bold leading-none mt-0.5 ${count > 0 ? a.numClr : "text-slate-300"}`} data-testid={`actionable-${a.key}-count`}>
                          {count}
                        </p>
                        {a.subtitle && <p className="text-[10px] text-slate-500 mt-0.5 truncate">{a.subtitle}</p>}
                      </div>
                    </button>
                  );
                })}
              </div>
            </section>

            {/* Favoris — grande rangée horizontale icône dessus / label dessous */}
            {favApps.length > 0 && (
              <section className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm" data-testid="section-favoris">
                <h2 className="text-sm font-bold text-slate-700 flex items-center gap-2 mb-4">
                  <Star className="w-4 h-4 text-amber-500 fill-amber-400" /> Favoris
                </h2>
                <div className="grid grid-cols-4 md:grid-cols-6 xl:grid-cols-8 gap-3">
                  {favApps.map((app) => {
                    const Icon = app.icon;
                    const count = app.badge ? badges[app.badge] : 0;
                    return (
                      <button
                        key={`fav-${app.key}`}
                        onClick={() => navigate(app.path)}
                        className="group relative flex flex-col items-center gap-2 p-3 rounded-xl hover:bg-slate-50 transition text-center"
                        data-testid={`favorite-${app.key}`}
                      >
                        {count > 0 && (
                          <span className="absolute top-1 right-1 min-w-[18px] h-5 px-1.5 rounded-full bg-indigo-600 text-white text-[10px] font-bold flex items-center justify-center shadow-sm">
                            {count > 99 ? "99+" : count}
                          </span>
                        )}
                        <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${app.tone} flex items-center justify-center group-hover:scale-110 transition-transform`}>
                          <Icon className={`w-6 h-6 ${app.iconClr}`} strokeWidth={2.2} />
                        </div>
                        <span className="text-xs font-semibold text-slate-700 leading-tight">{app.label}</span>
                      </button>
                    );
                  })}
                </div>
              </section>
            )}

            {/* Modules regroupés par catégorie — chaque catégorie est une vraie carte */}
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
              {catGroups.map((cat) => (
                <section key={cat.title} className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm" data-testid={`section-${cat.title.toLowerCase().replace(/\s|&/g, "-")}`}>
                  <h2 className="text-xs font-bold uppercase tracking-wider text-indigo-600 mb-4">{cat.title}</h2>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {cat.apps.map((app) => renderDesktopTile(app))}
                  </div>
                </section>
              ))}
            </div>

            {catGroups.length === 0 && (
              <div className="text-center py-16 text-slate-400">
                <LayoutDashboard className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>Aucun module ne correspond à votre recherche.</p>
              </div>
            )}
          </div>
        )}

        {/* ========== MOBILE + EDIT-MODE FALLBACK (grille aplatie) ========== */}
        <div className={editMode ? "" : "lg:hidden"} data-testid="apps-home-flat">
          <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 lg:grid-cols-7 xl:grid-cols-8 gap-2 sm:gap-3">
            {visibleApps.map((app) => renderTile(app))}
          </div>
          {visibleApps.length === 0 && (
            <div className="text-center py-16 text-slate-400">
              <LayoutDashboard className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>Aucun module ne correspond à votre recherche.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AppsHome;
